/**
 * The region generator (docs/archive/OPEN_WORLD.md §4.1, lane B): a `ChunkGenerator` that turns a region's
 * sketch, its core stamp (the village, byte for byte) and its blockout boxes into 64 m chunks.
 *
 * A chunk is a pure function of (sketch, stamps, ci, cj): the 4 m sketch cells round it are
 * evaluated from the features bucketed to it, heights are bilinear over the cells plus hashed
 * detail, then roads, the stone forest's spire tops, the core and its 48 m seam, and the boxes
 * are laid over them. Walls (`edge`) come from level differences over MAX_STEP, as `levelEdges`.
 * No DOM, timers or Math.random; no dependence on the order chunks are built in.
 */
import { Surface, Tile, MAX_STEP, type Facing } from '../map';
import { CHUNK, type ChunkData, type ChunkGenerator } from './chunk';
import {
  baseCell,
  SKETCH_CELL,
  compileSketch,
  coverOf,
  groundKind,
  polyNear,
  RK,
  smooth,
  vnoise,
  hash3,
  type BlockoutBox,
  type Cell,
  type CompiledSketch,
  type RegionKind,
  type RegionSketch,
} from './sketch';
import type { RegionStamp } from './stamp';

/** A box resolved to whole tiles and absolute levels. */
export interface ResolvedBox {
  readonly box: BlockoutBox;
  /** Tile rectangle [i0, i1) × [j0, j1). */
  readonly i0: number;
  readonly i1: number;
  readonly j0: number;
  readonly j1: number;
  /** Absolute levels (metres): the base, and the top (a ramp's high end; a pit's floor). */
  readonly base: number;
  readonly top: number;
  /** Whether the generator stamps it (a spire is the terrain itself). */
  readonly stamped: boolean;
}

/** What a box kind is stamped as: rule tile, variant, surface. */
const BOX_TILE: Readonly<Record<BlockoutBox['kind'], readonly [number, number, number]>> = {
  wall: [Tile.Wall, 0, Surface.Stone],
  gate: [Tile.Wall, 0, Surface.Stone],
  hall: [Tile.Wall, 0, Surface.Stone],
  house: [Tile.Wall, 0, Surface.Stone],
  tower: [Tile.Wall, 0, Surface.Stone],
  tier: [Tile.Wall, 0, Surface.Stone],
  spire: [Tile.Wall, 0, Surface.Stone],
  stone: [Tile.Wall, 0, Surface.Stone],
  ledge: [Tile.Wall, 0, Surface.Stone],
  station: [Tile.Wall, 0, Surface.Stone],
  ruin: [Tile.Wall, 0, Surface.Stone],
  mound: [Tile.Wall, 0, Surface.Earth],
  camp: [Tile.Wall, 1, Surface.Earth],
  tent: [Tile.Wall, 1, Surface.Earth],
  terrace: [Tile.Paved, 0, Surface.Stone],
  landing: [Tile.Paved, 0, Surface.Stone],
  ramp: [Tile.Paved, 1, Surface.Stone],
  pier: [Tile.Deck, 0, Surface.Deck],
  pit: [Tile.Floor, 0, Surface.Gravel],
};

/** Kinds walked on (open tiles); the rest are solid masses whose tops are stood on. */
export const WALKED_BOXES: ReadonlySet<BlockoutBox['kind']> = new Set(['terrace', 'landing', 'ramp', 'pier', 'pit']);

/** Each sketch kind as a rule tile, variant and surface (trees are scattered on top in `tileOf`). */
const KIND_TILE: Readonly<Record<number, readonly [number, number, number]>> = {
  [RK.SEA]: [Tile.Water, 0, Surface.Grass],
  [RK.SAND]: [Tile.Floor, 0, Surface.Sand],
  [RK.GRASS]: [Tile.Floor, 0, Surface.Grass],
  [RK.FOREST]: [Tile.Floor, 0, Surface.Grass],
  [RK.PINE]: [Tile.Floor, 0, Surface.Grass],
  [RK.BAMBOO]: [Tile.Floor, 0, Surface.Grass],
  [RK.FARM]: [Tile.Paddy, 0, Surface.Paddy],
  [RK.TEA]: [Tile.Floor, 0, Surface.Plots],
  [RK.FLOWER]: [Tile.Floor, 0, Surface.Grass],
  [RK.BLOSSOM]: [Tile.Floor, 0, Surface.Grass],
  // Bare rock and the spires' flat tops: stood on.
  [RK.ROCK]: [Tile.Floor, 0, Surface.Stone],
  [RK.CLIFF]: [Tile.Rock, 4, Surface.Grass],
  [RK.SPIRE]: [Tile.Rock, 4, Surface.Grass],
  [RK.RIVER]: [Tile.Water, 2, Surface.Grass],
  [RK.LAKE]: [Tile.Water, 1, Surface.Grass],
  [RK.ROAD]: [Tile.Floor, 0, Surface.Earth],
  [RK.TOWN]: [Tile.Paved, 0, Surface.Paving],
  [RK.BUILDING]: [Tile.Paved, 0, Surface.Paving],
  [RK.DECK]: [Tile.Deck, 0, Surface.Deck],
  [RK.REDROCK]: [Tile.Rock, 5, Surface.Grass],
  [RK.MARSH]: [Tile.Floor, 0, Surface.Moss],
  [RK.RUIN]: [Tile.Floor, 0, Surface.Gravel],
  [RK.CAMP]: [Tile.Floor, 0, Surface.Earth],
};

/** Metres over which the region's ground eases to the core's edge. */
export const SEAM = 48;

export interface BuiltRegion {
  readonly sketch: CompiledSketch;
  readonly core: RegionStamp;
  /** Every box (authored, then the sketch's spires, spire pagodas and stepping stones), resolved. */
  readonly boxes: readonly ResolvedBox[];
  readonly byId: ReadonlyMap<string, ResolvedBox>;
  /** The chunk generator (pure). */
  readonly generate: ChunkGenerator;
  /** The sketch kind (`RK`) of every tile of a chunk, row-major (for ground cover: lane H). */
  kindsOf(ci: number, cj: number): Uint8Array;
  /** The ground level (metres) of the tile under (x, y), boxes included unless `bare`. */
  levelAt(x: number, y: number, bare?: boolean): number;
  /** The rule tile under (x, y). */
  tileAt(x: number, y: number): number;
  /** The sketch kind under (x, y). */
  kindAt(x: number, y: number): RegionKind;
  /**
   * The far heightfield (lane C): the ground every `step` metres (4 or 8), sampled at the middle
   * of each step's square, as Int16 decimetres, with the sketch kind of each sample. Built once per
   * step and cached on the region (the same arrays every call: read them, never write).
   */
  farHeights(step: number): { step: number; n: number; dm: Int16Array; kind: Uint8Array };
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Builds a region: compiles the sketch, resolves the boxes and returns the generator. */
export function createRegion(sketchData: RegionSketch, blockout: readonly BlockoutBox[], core: RegionStamp): BuiltRegion {
  const C = compileSketch(sketchData);
  const S = sketchData.size;
  const nc = C.nc;

  // ── tile fill over a rectangle (the chunk plus a margin, or one point) ──
  interface Fill {
    lv: Float64Array;
    tile: Uint8Array;
    variant: Uint8Array;
    surface: Uint8Array;
    kind: Uint8Array;
    /** 1 + index into the core, or 0. */
    core: Int32Array;
    /** The water's surface (metres) on water tiles, else the level. */
    water: Float64Array;
  }

  /** Box lists per chunk (with a one-tile margin), built on first use. */
  const boxBuckets = new Map<number, ResolvedBox[]>();
  let boxes: ResolvedBox[] = [];
  const boxesNear = (ci: number, cj: number): ResolvedBox[] => {
    const key = cj * nc + ci;
    let l = boxBuckets.get(key);
    if (!l) {
      const x0 = ci * CHUNK - 1;
      const y0 = cj * CHUNK - 1;
      const x1 = (ci + 1) * CHUNK + 1;
      const y1 = (cj + 1) * CHUNK + 1;
      l = boxes.filter((b) => b.stamped && b.i1 > x0 && b.i0 < x1 && b.j1 > y0 && b.j0 < y1);
      boxBuckets.set(key, l);
    }
    return l;
  };

  /**
   * Fills tiles [X0, X0 + W·st) × [Y0, Y0 + H·st) with stride `st`, all inside chunk (ci, cj)'s
   * bucket margin.
   */
  function fill(ci: number, cj: number, X0: number, Y0: number, W: number, H: number, st: number, withBoxes: boolean): Fill {
    const b = C.bucket(ci, cj);
    // The 4 m cells under the tiles, with the cover pass's neighbours (±1, and 3 to the north).
    const cx0 = Math.floor(X0 / SKETCH_CELL) - 2;
    const cy0 = Math.floor(Y0 / SKETCH_CELL) - 4;
    const nx = Math.ceil((W * st) / SKETCH_CELL) + 5;
    const ny = Math.ceil((H * st) / SKETCH_CELL) + 7;
    const cells: Cell[] = new Array(nx * ny);
    for (let j = 0; j < ny; j++)
      for (let i = 0; i < nx; i++) cells[j * nx + i] = baseCell(C, b, (cx0 + i + 0.5) * SKETCH_CELL, (cy0 + j + 0.5) * SKETCH_CELL);
    const ckind = new Uint8Array(nx * ny);
    for (let n = 0; n < nx * ny; n++) ckind[n] = cells[n]!.kind;
    for (let j = 3; j < ny - 1; j++)
      for (let i = 1; i < nx - 1; i++) {
        const n = j * nx + i;
        const c = cells[n]!;
        const x = (cx0 + i + 0.5) * SKETCH_CELL;
        const y = (cy0 + j + 0.5) * SKETCH_CELL;
        let k = c.kind;
        if (k === RK.GRASS) {
          const slope =
            Math.max(Math.abs(cells[n + 1]!.h - cells[n - 1]!.h), Math.abs(cells[n + nx]!.h - cells[n - nx]!.h)) / (2 * SKETCH_CELL);
          k = coverOf(C, b, x, y, c.h, slope, cells[n + (C.s.flipped ? 3 : -3) * nx]?.kind === RK.SEA);
        }
        if (k !== RK.RIVER && k !== RK.LAKE) {
          const g = groundKind(b, x, y);
          if (g !== null) k = g;
        }
        ckind[n] = k;
      }
    const hAt = (x: number, y: number): number => {
      const fx = x / SKETCH_CELL - 0.5 - cx0;
      const fy = y / SKETCH_CELL - 0.5 - cy0;
      const ix = Math.floor(fx);
      const iy = Math.floor(fy);
      const tx = fx - ix;
      const ty = fy - iy;
      const a = cells[iy * nx + ix]!.h;
      const bb = cells[iy * nx + ix + 1]!.h;
      const c = cells[(iy + 1) * nx + ix]!.h;
      const d = cells[(iy + 1) * nx + ix + 1]!.h;
      return (a * (1 - tx) + bb * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
    };
    const N = W * H;
    const out: Fill = {
      lv: new Float64Array(N),
      tile: new Uint8Array(N),
      variant: new Uint8Array(N),
      surface: new Uint8Array(N),
      kind: new Uint8Array(N),
      core: new Int32Array(N),
      water: new Float64Array(N),
    };
    const nearBoxes = withBoxes ? boxesNear(ci, cj) : [];
    const { x: kx, y: ky, w: kw, h: kh } = core;
    for (let tj = 0; tj < H; tj++)
      for (let ti = 0; ti < W; ti++) {
        const n = tj * W + ti;
        const x = X0 + ti * st;
        const y = Y0 + tj * st;
        // The core, byte for byte.
        if (x >= kx && y >= ky && x < kx + kw && y < ky + kh) {
          const q = (y - ky) * kw + (x - kx);
          out.lv[n] = core.level[q]! / 100;
          out.tile[n] = core.tiles[q]!;
          out.variant[n] = core.variant[q]!;
          out.surface[n] = core.surface[q]!;
          out.kind[n] = RK.BUILDING;
          out.core[n] = q + 1;
          out.water[n] = out.lv[n]!;
          continue;
        }
        const px = x + 0.5;
        const py = y + 0.5;
        const cn = (Math.floor(y / SKETCH_CELL) - cy0) * nx + (Math.floor(x / SKETCH_CELL) - cx0);
        let k = ckind[cn]! as RegionKind;
        let h = hAt(px, py);
        const water = cells[cn]!.water;
        // The stone forest: flat tops on the spires, rock round them.
        for (const sp of b.spires) {
          const d = Math.hypot(px - sp.x, py - sp.y);
          if (d < sp.r * 0.7) {
            h = sp.top;
            k = RK.ROCK;
          } else if (d < sp.r) {
            h = Math.max(h, sp.top - (d / sp.r) * 6);
            k = RK.SPIRE;
          }
        }
        // Roads: level across, at the centreline's height; bridges over water.
        let road = false;
        if (k !== RK.SEA && k !== RK.SPIRE && k !== RK.ROCK && k !== RK.TOWN && k !== RK.CAMP && k !== RK.RUIN)
          for (const r of b.roads) {
            const near = polyNear(r.p, px, py, r.segs);
            if (near.d > (r.main ? 3.5 : 2.25)) continue;
            const hc = hAt(near.px, near.py);
            if (k === RK.RIVER || k === RK.LAKE || k === RK.MARSH) {
              h = Math.max(hc, water + 0.8);
              k = RK.DECK;
            } else {
              h = hc;
              k = RK.ROAD;
            }
            road = true;
            break;
          }
        const wet = k === RK.SEA || k === RK.RIVER || k === RK.LAKE;
        // Detail on natural ground.
        if (!road && !wet && k !== RK.TOWN && k !== RK.DECK && k !== RK.SPIRE && !(k === RK.ROCK && h > 0 && isSpireTop(b, px, py)))
          h += (vnoise(px / 9, py / 9, 3) - 0.5) * 0.6;
        // The seam: ease toward the core's edge over SEAM metres.
        if (!wet) {
          const d = Math.max(kx - x, x - (kx + kw - 1), ky - y, y - (ky + kh - 1));
          if (d > 0 && d < SEAM) {
            const qx = Math.min(kx + kw - 1, Math.max(kx, x)) - kx;
            const qy = Math.min(ky + kh - 1, Math.max(ky, y)) - ky;
            h = lerp(core.level[qy * kw + qx]! / 100, h, smooth(d / SEAM));
          }
        }
        const kt = KIND_TILE[k]!;
        let t = kt[0];
        let v = kt[1];
        let s = kt[2];
        // Trees, scattered by the tile's hash.
        if (t === Tile.Floor) {
          const r = hash3(x, y, 7);
          if (((k === RK.FOREST || k === RK.PINE) && r < 0.14) || (k === RK.BLOSSOM && r < 0.1) || (k === RK.GRASS && r < 0.01)) {
            t = Tile.Tree;
            v = 0;
          } else if (k === RK.BAMBOO && r < 0.3) {
            t = Tile.Tree;
            v = 1;
          }
        }
        // Paddies in plots with earth bunds.
        if (t === Tile.Paddy && (x % 24 === 0 || y % 16 === 0)) {
          t = Tile.Floor;
          v = 0;
          s = Surface.Earth;
        }
        // Boxes, later over earlier.
        for (const bx of nearBoxes) {
          if (x < bx.i0 || x >= bx.i1 || y < bx.j0 || y >= bx.j1) continue;
          const bt = BOX_TILE[bx.box.kind];
          t = bt[0];
          v = bt[1];
          s = bt[2];
          k = RK.BOX;
          h = bx.box.kind === 'ramp' ? rampLevel(bx, px, py) : bx.top;
        }
        out.lv[n] = h;
        // The water stands at its own surface over the bed (a skimmer's footing: map.ts `waterSurfaceAt`).
        out.water[n] = t === Tile.Water ? Math.max(h, water > -999 ? water : k === RK.SEA ? 0 : h) : h;
        out.tile[n] = t;
        out.variant[n] = v;
        out.surface[n] = s;
        out.kind[n] = k;
      }
    return out;
  }

  const isSpireTop = (b: ReturnType<CompiledSketch['bucket']>, px: number, py: number): boolean =>
    b.spires.some((sp) => Math.hypot(px - sp.x, py - sp.y) < sp.r * 0.7);

  /** One tile, exactly as its chunk builds it. */
  const sample = (x: number, y: number, withBoxes: boolean): Fill => {
    const xi = Math.min(S - 1, Math.max(0, Math.floor(x)));
    const yi = Math.min(S - 1, Math.max(0, Math.floor(y)));
    return fill(Math.floor(xi / CHUNK), Math.floor(yi / CHUNK), xi, yi, 1, 1, 1, withBoxes);
  };

  // ── boxes ──
  const resolved = new Map<string, ResolvedBox>();
  const resolve = (bx: BlockoutBox, stamped = true): ResolvedBox => {
    if (resolved.has(bx.id)) throw new Error(`blockout: duplicate box id ${bx.id}`);
    const i0 = Math.round(bx.x - bx.w / 2);
    const j0 = Math.round(bx.y - bx.d / 2);
    const on = bx.on ? resolved.get(bx.on) : undefined;
    if (bx.on && !on) throw new Error(`blockout: ${bx.id} stands on ${bx.on}, which is not before it`);
    const base = bx.base ?? on?.top ?? sample(bx.x, bx.y, false).lv[0]!;
    const r: ResolvedBox = { box: bx, i0, i1: i0 + Math.round(bx.w), j0, j1: j0 + Math.round(bx.d), base, top: base + bx.h, stamped };
    resolved.set(bx.id, r);
    return r;
  };
  const list: ResolvedBox[] = blockout.map((b) => resolve(b));
  // The stone forest's spires (terrain: listed, not stamped) and the pagodas on some of them.
  for (const sp of C.spires) {
    const s = Math.round(sp.r * 2 * 0.7);
    const forest = sp.id.startsWith('stoneforest');
    list.push(
      resolve(
        {
          id: sp.id,
          place: forest ? 6 : 0,
          name: forest ? 'ป่าเสาหินเมฆา เสาหิน' : 'กองหินกลางทะเล',
          lane: forest ? 'stoneforest' : 'islands',
          kind: 'spire',
          x: sp.x,
          y: sp.y,
          w: s,
          d: s,
          h: 0,
          base: sp.top,
          note: `terrain: a flat standable top ${(sp.r * 1.4).toFixed(0)} m across at ${sp.top.toFixed(0)} m`,
        },
        false,
      ),
    );
    if (sp.pagoda)
      list.push(
        resolve({
          id: sp.id.replace('spire', 'pagoda'),
          place: 6,
          name: 'ป่าเสาหินเมฆา เจดีย์บนเสาหิน',
          lane: 'stoneforest',
          kind: 'tier',
          x: sp.x,
          y: sp.y,
          w: Math.min(8, Math.round(sp.r)),
          d: Math.min(8, Math.round(sp.r)),
          h: 14,
          on: sp.id,
        }),
      );
  }
  // The bay's stepping stones: 0.7 m over the sea, so a skimmer steps up onto them (water skim).
  // (Only on the sea: a leg's end on an island would sink a stone into its ground.)
  const seaAt = (x: number, y: number): boolean => {
    const cx = (Math.floor(x / SKETCH_CELL) + 0.5) * SKETCH_CELL;
    const cy = (Math.floor(y / SKETCH_CELL) + 0.5) * SKETCH_CELL;
    return baseCell(C, C.bucket(Math.floor(cx / CHUNK), Math.floor(cy / CHUNK)), cx, cy).kind === RK.SEA;
  };
  C.stones.forEach(([x, y], k) => {
    if (!seaAt(x, y)) return;
    list.push(
      resolve({
        id: `bay.stone.${String(k + 1).padStart(2, '0')}`,
        place: 0,
        name: 'หินเหยียบข้ามอ่าว',
        lane: 'islands',
        kind: 'stone',
        x,
        y,
        w: 4,
        d: 4,
        h: 0.7,
        base: 0,
      }),
    );
  });
  boxes = list;

  // ── chunks ──
  const build = (ci: number, cj: number): { data: ChunkData; kinds: Uint8Array } => {
    const X0 = ci * CHUNK;
    const Y0 = cj * CHUNK;
    const W = CHUNK + 1;
    const f = fill(ci, cj, X0, Y0, W, W, 1, true);
    const n = CHUNK * CHUNK;
    const tiles = new Uint8Array(n);
    const variant = new Uint8Array(n);
    const surface = new Uint8Array(n);
    const kinds = new Uint8Array(n);
    const level = new Int16Array(n);
    const edge = new Uint8Array(n);
    let wet = false;
    let lo = Infinity;
    for (let j = 0; j < CHUNK; j++)
      for (let i = 0; i < CHUNK; i++) {
        const p = j * W + i;
        const q = j * CHUNK + i;
        tiles[q] = f.tile[p]!;
        variant[q] = f.variant[p]!;
        surface[q] = f.surface[p]!;
        kinds[q] = f.kind[p]!;
        if (f.tile[p] === Tile.Water) wet = true;
        lo = Math.min(lo, Math.round(f.lv[p]! * 100));
      }
    const base = lo;
    const cm = new Int32Array(W * W);
    for (let p = 0; p < W * W; p++) cm[p] = Math.round(f.lv[p]! * 100);
    const step = Math.round(MAX_STEP * 100);
    for (let j = 0; j < CHUNK; j++)
      for (let i = 0; i < CHUNK; i++) {
        const p = j * W + i;
        const q = j * CHUNK + i;
        const v = cm[p]! - base;
        if (v > 32767) throw new Error(`region: chunk ${ci},${cj} spans over 327 m`);
        level[q] = v;
        if (f.tile[p] === Tile.Water) continue;
        const cp = f.core[p]!;
        let e = 0;
        // East (bit 1) and south (bit 2) sides: a wall where the levels differ by more than a step;
        // inside the core, the core's own walls.
        const pe = p + 1;
        if (f.tile[pe] !== Tile.Water) {
          if (cp && f.core[pe]) e |= core.edge[cp - 1]! & 1;
          else if (Math.abs(cm[pe]! - cm[p]!) > step) e |= 1;
        }
        const ps = p + W;
        if (f.tile[ps] !== Tile.Water) {
          if (cp && f.core[ps]) e |= core.edge[cp - 1]! & 2;
          else if (Math.abs(cm[ps]! - cm[p]!) > step) e |= 2;
        }
        edge[q] = e;
      }
    const data: {
      -readonly [K in keyof ChunkData]: ChunkData[K];
    } = { ci, cj, tiles, variant, base, level, edge, surface };
    if (wet) {
      const water = new Int16Array(n);
      for (let j = 0; j < CHUNK; j++)
        for (let i = 0; i < CHUNK; i++) {
          const p = j * W + i;
          if (f.tile[p] === Tile.Water) water[j * CHUNK + i] = Math.min(32767, Math.round(f.water[p]! * 100) - base);
        }
      data.water = water;
    }
    // The core's authored layers, where the chunk holds any of it.
    const { x: kx, y: ky, w: kw, h: kh } = core;
    if (X0 < kx + kw && X0 + CHUNK > kx && Y0 < ky + kh && Y0 + CHUNK > ky) {
      const owner = new Uint16Array(n);
      const flight = new Uint16Array(n);
      const safe = new Uint8Array(n);
      const made = new Uint8Array(n);
      const deep = new Uint8Array(n);
      for (let j = 0; j < CHUNK; j++)
        for (let i = 0; i < CHUNK; i++) {
          const cp = f.core[j * W + i]!;
          if (!cp) continue;
          const q = j * CHUNK + i;
          owner[q] = core.owner[cp - 1]!;
          flight[q] = core.flight[cp - 1]!;
          safe[q] = core.safe[cp - 1]!;
          made[q] = core.made[cp - 1]!;
          deep[q] = core.deep[cp - 1]!;
        }
      Object.assign(data, { owner, flight, safe, made, deep });
    }
    return { data, kinds };
  };

  // farHeights per step, built on first ask (about a second each).
  const far = new Map<number, ReturnType<BuiltRegion['farHeights']>>();
  const region: BuiltRegion = {
    sketch: C,
    core,
    boxes,
    byId: resolved,
    generate: (ci, cj) => build(ci, cj).data,
    kindsOf: (ci, cj) => build(ci, cj).kinds,
    levelAt: (x, y, bare = false) => sample(x, y, !bare).lv[0]!,
    tileAt: (x, y) => sample(x, y, true).tile[0]!,
    kindAt: (x, y) => sample(x, y, true).kind[0]! as RegionKind,
    farHeights(step) {
      const had = far.get(step);
      if (had) return had;
      if (CHUNK % step) throw new Error(`farHeights: step ${step} must divide ${CHUNK}`);
      const n = S / step;
      const per = CHUNK / step;
      const dm = new Int16Array(n * n);
      const kind = new Uint8Array(n * n);
      for (let cj = 0; cj < nc; cj++)
        for (let ci = 0; ci < nc; ci++) {
          const f = fill(ci, cj, ci * CHUNK + (step >> 1), cj * CHUNK + (step >> 1), per, per, step, true);
          for (let b = 0; b < per; b++)
            for (let a = 0; a < per; a++) {
              const q = (cj * per + b) * n + ci * per + a;
              dm[q] = Math.round(f.lv[b * per + a]! * 10);
              kind[q] = f.kind[b * per + a]!;
            }
        }
      const out = { step, n, dm, kind };
      far.set(step, out);
      return out;
    },
  };
  return region;
}

/** A ramp's level at a point: from its base at the `down` side to its top at the far side. */
function rampLevel(b: ResolvedBox, x: number, y: number): number {
  const f: Facing = b.box.down ?? 's';
  const t =
    f === 'e'
      ? (b.i1 - x) / (b.i1 - b.i0)
      : f === 'w'
        ? (x - b.i0) / (b.i1 - b.i0)
        : f === 's'
          ? (b.j1 - y) / (b.j1 - b.j0)
          : (y - b.j0) / (b.j1 - b.j0);
  return b.base + (b.top - b.base) * Math.min(1, Math.max(0, t));
}
